import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, Snackbar, Alert, CircularProgress } from '@mui/material';

const GOOGLE_CLIENT_ID   = process.env.REACT_APP_GOOGLE_CLIENT_ID;

// Robust configuration detection
const IS_FLAG_ENABLED    = process.env.REACT_APP_ENABLE_GOOGLE_AUTH === 'true' || process.env.REACT_APP_ENABLE_GOOGLE_AUTH === true;
const IS_CLIENT_ID_VALID = !!(
    GOOGLE_CLIENT_ID &&
    typeof GOOGLE_CLIENT_ID === 'string' &&
    GOOGLE_CLIENT_ID.trim().length > 10 &&
    GOOGLE_CLIENT_ID.includes('.apps.googleusercontent.com')
);

const ENABLE_GOOGLE_AUTH = IS_FLAG_ENABLED && !!GOOGLE_CLIENT_ID;
const IS_CONFIGURED      = ENABLE_GOOGLE_AUTH;

console.log('[GoogleAuth] Module loaded — ENABLE_GOOGLE_AUTH:', ENABLE_GOOGLE_AUTH,
    '| GOOGLE_CLIENT_ID set:', !!GOOGLE_CLIENT_ID,
    '| IS_CLIENT_ID_VALID:', IS_CLIENT_ID_VALID,
    '| IS_CONFIGURED:', IS_CONFIGURED);

const UNCONFIGURED_MSG =
    'Google Sign-In is currently being configured. Please use email login.';

const NO_CREDENTIALS_MSG =
    'Google authentication credentials are not configured.';

// Official Google "G" multicolor logo
function GoogleLogo({ size = 20 }) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            width={size}
            height={size}
            aria-hidden="true"
            style={{ flexShrink: 0 }}
        >
            <path
                fill="#4285F4"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            />
            <path
                fill="#34A853"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            />
            <path
                fill="#FBBC05"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
            />
            <path
                fill="#EA4335"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            />
        </svg>
    );
}

const LABEL_MAP = {
    continue_with: 'Google',
    signin_with:   'Google',
    signup_with:   'Google',
};

/**
 * GoogleSignInButton
 * ------------------
 * Always renders a Google Sign-In button regardless of configuration state.
 *
 * When REACT_APP_ENABLE_GOOGLE_AUTH=true AND REACT_APP_GOOGLE_CLIENT_ID is set:
 *   → Uses Google Identity Services (GIS) native button for real OAuth.
 *
 * Otherwise (no credentials / feature flag off):
 *   → Renders a custom Google-styled button.
 *   → On click, shows an informational Snackbar toast.
 *   → Never crashes, never hides, never throws console errors.
 *   → ALWAYS produces a visible result on click — never silently ignores.
 *
 * Props:
 *   onCredential(token: string)  — called with the raw Google ID token (configured mode only)
 *   disabled (bool)              — passed down for visual styling only; click always fires
 *   text     (string)            — 'continue_with' | 'signin_with' | 'signup_with'
 */
export default function GoogleSignInButton({
                                               onCredential,
                                               disabled = false,
                                               text     = 'continue_with',
                                           }) {
    const containerRef    = useRef(null);
    const [toastOpen,    setToastOpen]    = useState(false);
    const [toastMsg,     setToastMsg]     = useState(UNCONFIGURED_MSG);
    const [toastSeverity,setToastSeverity]= useState('info');
    const [gisLoaded,      setGisLoaded]    = useState(false);
    const [gisLoading,     setGisLoading]   = useState(IS_CONFIGURED);
    const [renderFallback, setRenderFallback] = useState(!IS_CONFIGURED);
    const renderedRef = useRef(null);

    const label = LABEL_MAP[text] || 'Continue with Google';

    // ── Effect 0: Fix Cross-Origin-Opener-Policy for Google Sign-In popup ─────
    useEffect(() => {
        // Inject COOP meta tag so Google OAuth popup can postMessage back to this page.
        // This is needed when the server sends `Cross-Origin-Opener-Policy: same-origin`
        // (e.g. via CloudFront), which would otherwise block the OAuth callback.
        const existingMeta = document.querySelector('meta[http-equiv="Cross-Origin-Opener-Policy"]');
        if (!existingMeta) {
            const meta = document.createElement('meta');
            meta.httpEquiv = 'Cross-Origin-Opener-Policy';
            meta.content = 'same-origin-allow-popups';
            document.head.appendChild(meta);
            console.log('[GoogleAuth] COOP meta tag injected');
        }

        // Also inject COEP as unsafe-none to avoid embedder policy conflicts
        const existingCoep = document.querySelector('meta[http-equiv="Cross-Origin-Embedder-Policy"]');
        if (!existingCoep) {
            const coepMeta = document.createElement('meta');
            coepMeta.httpEquiv = 'Cross-Origin-Embedder-Policy';
            coepMeta.content = 'unsafe-none';
            document.head.appendChild(coepMeta);
            console.log('[GoogleAuth] COEP meta tag injected');
        }
    }, []);

    // ── Effect 1: Script Loading & GIS Availability ──────────────────────────
    useEffect(() => {
        if (!IS_CONFIGURED) {
            setRenderFallback(true);
            setGisLoading(false);
            return;
        }

        let attempts = 0;
        const MAX_ATTEMPTS = 30; // 7.5 seconds

        const checkGis = () => {
            if (window.google?.accounts?.id) {
                console.log('[GoogleAuth] GIS script detected');
                setGisLoaded(true);
                setGisLoading(false);
            } else if (attempts++ < MAX_ATTEMPTS) {
                setTimeout(checkGis, 250);
            } else {
                console.warn('[GoogleAuth] GIS script failed to load');
                setRenderFallback(true);
                setGisLoading(false);
            }
        };

        checkGis();
    }, []);

    // ── Effect 2: Initialization & Rendering ──────────────────────────────────
    useEffect(() => {
        // Only proceed if GIS is loaded, loader is gone, and container is mounted
        if (!gisLoaded || gisLoading || renderFallback || !containerRef.current) return;

        // Avoid double rendering if same text and onCredential
        const renderKey = `${text}`;
        if (renderedRef.current === renderKey) return;

        console.log('[GoogleAuth] Rendering native button');

        try {
            // 1. Initialize with a single configuration object
            const idConfig = {
                client_id:             GOOGLE_CLIENT_ID,
                callback:              async (response) => {
                    if (response?.credential && onCredential) {
                        try {
                            await onCredential(response.credential);
                        } catch (err) {
                            console.error('[GoogleAuth] onCredential error:', err);
                            const msg = err.response?.data?.message || err.message || 'Google authentication failed';
                            setToastMsg(msg);
                            setToastSeverity('error');
                            setToastOpen(true);
                        }
                    }
                },
                auto_select:           false,
                cancel_on_tap_outside: true,
            };
            window.google.accounts.id.initialize(idConfig);

            // 2. Render button with element and configuration object
            const buttonConfig = {
                type:           'standard',
                theme:          'outline',
                size:           'large',
                text:           text,
                shape:          'rectangular',
                width:          containerRef.current.offsetWidth || 400,
                logo_alignment: 'left',
            };
            window.google.accounts.id.renderButton(containerRef.current, buttonConfig);
            renderedRef.current = renderKey;

            // 3. Post-render check (e.g. adblocker)
            setTimeout(() => {
                if (containerRef.current && containerRef.current.children.length === 0) {
                    console.warn('[GoogleAuth] Container empty after render. Switching to fallback.');
                    setRenderFallback(true);
                }
            }, 2000);

        } catch (err) {
            console.error('[GoogleAuth] Initialization/Render error:', err);
            setRenderFallback(true);
        }
    }, [gisLoaded, gisLoading, renderFallback, text, onCredential]);

    const handleFallbackClick = () => {
        console.log('[GoogleAuth] Fallback button clicked');

        // If GIS is actually loaded but we are in fallback mode (e.g. adblocker
        // blocked the button but not the script), try to trigger One Tap.
        if (window.google?.accounts?.id) {
            try {
                window.google.accounts.id.prompt();
            } catch (err) {
                console.error('[GoogleAuth] prompt() error:', err);
                setToastMsg(UNCONFIGURED_MSG);
                setToastSeverity('info');
                setToastOpen(true);
            }
        } else {
            setToastMsg(UNCONFIGURED_MSG);
            setToastSeverity('info');
            setToastOpen(true);
        }
    };

    // ── Unconfigured path: custom button + toast ──────────────────────────────
    // NOTE: The button intentionally does NOT receive `disabled` as a prop.
    // MUI's disabled prop sets pointer-events:none, causing completely silent
    // click failures with no user feedback. Instead, we always fire the handler
    // and show the informational toast. Visual dimming is applied via sx only.
    if (renderFallback) {
        return (
            <>
                <Button
                    fullWidth
                    variant="outlined"
                    onClick={handleFallbackClick}
                    startIcon={<GoogleLogo size={18} />}
                    aria-label="Continue with Google"
                    sx={{
                        borderColor:   '#dadce0',
                        color:         '#3c4043',
                        backgroundColor: '#ffffff !important',
                        fontFamily:    '"Google Sans", Roboto, Arial, sans-serif',
                        fontWeight:    500,
                        fontSize:      '0.875rem',
                        textTransform: 'none',
                        letterSpacing: '0.25px',
                        py:            1.25,
                        px:            3,
                        borderRadius:  '4px',
                        justifyContent: 'center',
                        opacity:       disabled ? 0.6 : 1,
                        transition:    'box-shadow 0.15s, background-color 0.15s, opacity 0.15s',
                        '&:hover': {
                            borderColor:     '#d2e3fc',
                            backgroundColor: '#f8fbff !important',
                            boxShadow:       '0 1px 3px rgba(60,64,67,0.2)',
                        },
                        '&:active': {
                            backgroundColor: '#f1f3f4 !important',
                            boxShadow:       'none',
                        },
                        // Dark mode (removed black background)
                        '@media (prefers-color-scheme: dark)': {
                            borderColor:     '#dadce0',
                            color:           '#3c4043',
                            backgroundColor: '#ffffff !important',
                            '&:hover': {
                                backgroundColor: '#f8fbff !important',
                                borderColor:     '#d2e3fc',
                            },
                        },
                    }}
                >
                    {label}
                </Button>

                <Snackbar
                    open={toastOpen}
                    autoHideDuration={5000}
                    onClose={() => setToastOpen(false)}
                    anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                >
                    <Alert
                        severity={toastSeverity}
                        onClose={() => setToastOpen(false)}
                        sx={{ width: '100%' }}
                    >
                        {toastMsg}
                    </Alert>
                </Snackbar>
            </>
        );
    }

    // ── Configured path: GIS native button container ──────────────────────────
    if (gisLoading) {
        return (
            <Box
                sx={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    py: 1.25,
                    opacity: disabled ? 0.5 : 1,
                }}
            >
                <CircularProgress size={24} sx={{ color: '#4285F4' }} />
            </Box>
        );
    }

    return (
        <>
            <Box
                sx={{
                    width: '100%',
                    opacity:       disabled ? 0.5 : 1,
                    pointerEvents: disabled ? 'none' : 'auto',
                }}
            >
                <Box
                    ref={containerRef}
                    sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}
                />
            </Box>

            <Snackbar
                open={toastOpen}
                autoHideDuration={5000}
                onClose={() => setToastOpen(false)}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert
                    severity={toastSeverity}
                    onClose={() => setToastOpen(false)}
                    sx={{ width: '100%' }}
                >
                    {toastMsg}
                </Alert>
            </Snackbar>
        </>
    );
}